import {
  forwardRef,
  useId,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { AutocompleteProps } from './props.js'

export const Autocomplete = forwardRef<
  HTMLInputElement,
  AutocompleteProps<any>
>(
  (
    {
      label,
      value,
      onChange,
      onSelect,
      fetchOptions,
      getOptionLabel,
      getOptionKey,
      renderOption,
      placeholder,
      error,
      supportingText,
      disabled = false,
      bare = false,
      openOnFocus = false,
      autoFocus = false,
      onKeyDown,
      className = '',
    },
    ref
  ) => {
    const id = useId()
    const supportingId = useId()
    const listboxId = useId()
    const inputRef = useRef<HTMLInputElement>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [options, setOptions] = useState<any[]>([])
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [isLoading, setIsLoading] = useState(false)
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const fetchAbortController = useRef<AbortController | null>(null)
    const currentRequestId = useRef<number>(0)

    // Merge refs
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(inputRef.current)
        } else {
          ref.current = inputRef.current
        }
      }
    }, [ref])

    // Debounced fetch
    const debouncedFetch = useCallback(
      (query: string) => {
        setHighlightedIndex(-1)

        // An empty query normally means "nothing to search for yet". With
        // openOnFocus the empty query is meaningful: it asks for the whole
        // (small) set, so it falls through to the fetch below.
        if (!query.trim() && !openOnFocus) {
          setOptions([])
          setIsOpen(false)
          return
        }

        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current)
        }
        setIsLoading(true)
        const requestId = ++currentRequestId.current

        debounceTimer.current = setTimeout(async () => {
          try {
            if (fetchAbortController.current) {
              fetchAbortController.current.abort()
            }

            const results = await fetchOptions(query)

            // Ignore results if a newer request was made
            if (requestId !== currentRequestId.current) {
              return
            }

            setOptions(results)
            setIsOpen(results.length > 0)
            setIsLoading(false)
          } catch {
            if (requestId === currentRequestId.current) {
              setIsLoading(false)
              setOptions([])
            }
          }
        }, 250)
      },
      [fetchOptions, openOnFocus]
    )

    const handleChange = (newValue: string) => {
      onChange(newValue)
      debouncedFetch(newValue)
    }

    const handleSelect = (option: any) => {
      onChange(getOptionLabel(option))
      onSelect(option)
      setIsOpen(false)
      setOptions([])
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        onKeyDown?.(e)
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((i) =>
          i < options.length - 1 ? i + 1 : options.length - 1
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((i) => (i > 0 ? i - 1 : -1))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex])
        } else {
          setIsOpen(false)
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
      } else {
        onKeyDown?.(e)
      }
    }

    const isError = !!error

    if (bare) {
      return (
        <div className="relative w-full">
          <input
            ref={inputRef}
            id={id}
            type="text"
            autoFocus={autoFocus}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => (openOnFocus ? debouncedFetch(value) : value && setIsOpen(true))}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${listboxId}-option-${highlightedIndex}`
                : undefined
            }
            className={`
              w-full h-8 px-3 rounded-sm bg-surface-high border border-outline outline-none
              text-on-surface placeholder:text-on-surface-subtle
              interactive hover:bg-surface-highest hover:border-on-surface-subtle
              focus:bg-surface-highest focus:border-on-surface focus:ring-1 focus:ring-on-surface
              ${className}
            `}
          />
          {isOpen && (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute top-full start-0 end-0 mt-1 bg-surface-container border border-outline-variant rounded-md shadow-elev-2 z-40"
            >
              {options.map((option, index) => (
                <li
                  key={getOptionKey(option)}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onClick={() => handleSelect(option)}
                  className={`
                    px-4 py-2 cursor-pointer transition-colors
                    ${
                      index === highlightedIndex
                        ? 'bg-primary text-on-primary'
                        : 'text-on-surface hover:bg-surface-low'
                    }
                  `}
                >
                  {renderOption
                    ? renderOption(option)
                    : getOptionLabel(option)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )
    }

    const containerClasses = [
      'flex flex-col gap-0 w-full relative',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const inputWrapperClasses = [
      'relative flex items-center',
      'bg-surface-low border border-outline-variant rounded-md',
      'interactive',
      isError && 'border-error',
      disabled && 'opacity-38',
    ]
      .filter(Boolean)
      .join(' ')

    const inputClasses = [
      'flex-1 bg-transparent outline-none px-3 py-2 h-9',
      'text-body-md text-on-surface placeholder:text-on-surface-subtle',
    ]
      .filter(Boolean)
      .join(' ')

    const labelClasses = 'text-label-md text-on-surface-variant mb-1.5 block'

    const supportingClasses = [
      'text-body-sm mt-1.5',
      isError ? 'text-error' : 'text-on-surface-variant',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div className={containerClasses}>
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
        <div className={inputWrapperClasses}>
          <input
            ref={inputRef}
            id={id}
            type="text"
            autoFocus={autoFocus}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => {
              // openOnFocus lists the whole (small) set straight away; without
              // it, focus only reopens a list the user has already typed for.
              if (openOnFocus) debouncedFetch(value)
              else if (value) setIsOpen(true)
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={isError}
            aria-describedby={isError ? supportingId : undefined}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${listboxId}-option-${highlightedIndex}`
                : undefined
            }
            className={inputClasses}
          />
          {isLoading && (
            <div className="px-3 text-on-surface-variant flex-shrink-0">
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.2" />
                <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          )}
          {isOpen && (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute top-full start-0 end-0 mt-1 bg-surface-container border border-outline-variant rounded-lg shadow-elev-3 z-50 py-1 max-h-64 overflow-auto"
            >
              {options.map((option, index) => (
                <li
                  key={getOptionKey(option)}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onClick={() => handleSelect(option)}
                  className={`
                    px-3 py-2 cursor-pointer transition-colors text-body-md
                    ${
                      index === highlightedIndex
                        ? 'bg-surface-high text-on-surface'
                        : 'text-on-surface'
                    }
                  `}
                >
                  {renderOption
                    ? renderOption(option)
                    : getOptionLabel(option)}
                </li>
              ))}
            </ul>
          )}
        </div>
        {(error || supportingText) && (
          <p
            id={supportingId}
            className={supportingClasses}
          >
            {error || supportingText}
          </p>
        )}
      </div>
    )
  }
)

Autocomplete.displayName = 'Autocomplete'
